(() => {
  const ROOT_SEL = '[data-section-type="search"]';
  const INPUT_SEL = '[data-search-input]';
  const FORM_SEL = '.search-form';
  const SEARCH_SEL = '.search';
  const TITLE_SEL = '.search-header__title';
  const LOADER_SEL = '[data-loading]';
  const ACTIVE_CLASS = 'is-active';
  const DEBOUNCE_MS = 320;

  let debounceId = null;
  let abortController = null;
  let requestId = 0;
  let initialized = false;

  function getRoot() {
    return document.querySelector(ROOT_SEL);
  }

  function buildParams(term) {
    const params = new URLSearchParams();
    const trimmed = (term || '').trim();

    if (trimmed !== '') {
      params.set('q', trimmed);
    }

    params.set('options[prefix]', 'last');
    return params;
  }

  function setLoader(root, isActive) {
    const loader = root.querySelector(LOADER_SEL);
    if (!loader) return;
    loader.classList.toggle(ACTIVE_CLASS, isActive);
  }

  function unloadSearchInstance(container) {
    const api = window.Shopify && window.Shopify.theme && window.Shopify.theme.sections;
    if (!api || !Array.isArray(api.instances)) return;

    for (let i = api.instances.length - 1; i >= 0; i -= 1) {
      const instance = api.instances[i];
      if (instance.type !== 'search' && instance.container !== container) continue;

      try {
        if (typeof instance.onUnload === 'function') instance.onUnload();
      } catch (error) {
        // Keep live search resilient if theme unload fails.
      }

      api.instances.splice(i, 1);
    }
  }

  function loadSearchInstance(container) {
    const api = window.Shopify && window.Shopify.theme && window.Shopify.theme.sections;
    if (!api || !api.registered || !api.registered.search) return;

    try {
      api.instances.push(new api.registered.search(container));
    } catch (error) {
      // Keep results visible even if theme re-init fails.
    }
  }

  function syncDatasets(currentRoot, nextRoot) {
    if (!currentRoot || !nextRoot) return;

    ['searchItemCount', 'searchTerm', 'paginationType', 'collectionColumnsDesktop', 'layoutType'].forEach(
      (key) => {
        if (nextRoot.dataset[key] !== undefined) {
          currentRoot.dataset[key] = nextRoot.dataset[key];
        }
      }
    );
  }

  function updateHistory(params) {
    const query = params.toString();
    const url = query
      ? `${window.location.pathname}?${query}`
      : window.location.pathname;

    history.pushState({ vpSearchLive: true, searchParams: query }, '', url);
  }

  function renderFromDocument(root, doc, params, pushHistory) {
    const nextRoot = doc.querySelector(ROOT_SEL);
    if (!nextRoot) return;

    const currentTitle = root.querySelector(TITLE_SEL);
    const nextTitle = nextRoot.querySelector(TITLE_SEL);
    if (currentTitle && nextTitle) {
      currentTitle.innerHTML = nextTitle.innerHTML;
    }

    const currentSearch = root.querySelector(SEARCH_SEL);
    const nextSearch = nextRoot.querySelector(SEARCH_SEL);
    if (currentSearch && nextSearch) {
      currentSearch.className = nextSearch.className;
      currentSearch.innerHTML = nextSearch.innerHTML;

      Array.from(nextSearch.attributes).forEach((attr) => {
        if (attr.name === 'class') return;
        currentSearch.setAttribute(attr.name, attr.value);
      });
    }

    syncDatasets(root, nextRoot);
    unloadSearchInstance(root);
    loadSearchInstance(root);

    if (pushHistory) {
      updateHistory(params);
    }

    document.dispatchEvent(new CustomEvent('collection:updated'));
  }

  function fetchResults(term, pushHistory) {
    const root = getRoot();
    if (!root || !root.dataset.sectionId) return;

    const params = buildParams(term);
    const currentId = (requestId += 1);

    if (abortController) abortController.abort();
    abortController = new AbortController();

    setLoader(root, true);

    const url = `${window.location.pathname}?section_id=${encodeURIComponent(
      root.dataset.sectionId
    )}&${params.toString()}`;

    fetch(url, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Search request failed');
        return response.text();
      })
      .then((html) => {
        if (currentId !== requestId) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        renderFromDocument(root, doc, params, pushHistory);
      })
      .catch((error) => {
        if (error && error.name === 'AbortError') return;
      })
      .finally(() => {
        if (currentId === requestId) {
          const latestRoot = getRoot();
          if (latestRoot) setLoader(latestRoot, false);
        }
      });
  }

  function scheduleFetch(term) {
    window.clearTimeout(debounceId);
    debounceId = window.setTimeout(() => {
      fetchResults(term, true);
    }, DEBOUNCE_MS);
  }

  function onInput(event) {
    scheduleFetch(event.target.value);
  }

  function onSubmit(event) {
    event.preventDefault();
    const root = getRoot();
    if (!root) return;
    const input = root.querySelector(INPUT_SEL);
    window.clearTimeout(debounceId);
    fetchResults(input ? input.value : '', true);
  }

  function onPopState() {
    const root = getRoot();
    if (!root) return;
    const input = root.querySelector(INPUT_SEL);
    if (!input) return;

    const params = new URLSearchParams(window.location.search);
    const term = params.get('q') || '';
    if (input.value !== term) input.value = term;
    window.clearTimeout(debounceId);
    fetchResults(term, false);
  }

  function init() {
    if (initialized) return;
    const root = getRoot();
    if (!root) return;

    const input = root.querySelector(INPUT_SEL);
    const form = root.querySelector(FORM_SEL);
    if (!input || !form) return;

    initialized = true;
    input.addEventListener('input', onInput);
    form.addEventListener('submit', onSubmit);
    window.addEventListener('popstate', onPopState);
  }

  function onSectionLoad(event) {
    const target = event && event.target;
    if (!target || !target.querySelector) return;
    if (!target.querySelector(ROOT_SEL) && !target.matches?.(ROOT_SEL)) return;
    initialized = false;
    init();
  }

  function onSectionUnload(event) {
    const target = event && event.target;
    if (!target || !target.querySelector) return;
    if (!target.querySelector(ROOT_SEL) && !target.matches?.(ROOT_SEL)) return;

    window.clearTimeout(debounceId);
    if (abortController) abortController.abort();
    initialized = false;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);
})();
